/**
 * WordBonuses — central registry of word-based multipliers.
 *
 * All scaling that depends on word properties (length, letter rarity, etc.)
 * lives here. Both DamageCalculator and XPTable call into this module
 * so the numbers stay in one place.
 */

/**
 * Damage/XP multiplier based on word length.
 *
 *   2–4 letters → 1.0× (no bonus)
 *   5 letters   → 1.2×
 *   6 letters   → 1.4×
 *   7 letters   → 1.6×
 *   8 letters   → 1.8×
 *   9+ letters  → 2.0×
 *
 * Linear ramp: 1.0 + max(0, len - 4) × 0.2, capped at 2.0.
 */
export function lengthMultiplier(wordLength: number): number {
  if (wordLength <= 4) return 1;
  return Math.min(1 + (wordLength - 4) * 0.2, 2);
}

/**
 * Count consecutive adjacent pairs in a tile selection.
 * Two tiles are adjacent if they differ by at most 1 in both row and col
 * (includes diagonals — 8-directional adjacency).
 * Returns 0..selection.length-1 (the number of adjacent links in the chain).
 */
export function countAdjacentPairs(tiles: { row: number; col: number }[]): number {
  let count = 0;
  for (let i = 1; i < tiles.length; i++) {
    const dr = Math.abs(tiles[i].row - tiles[i - 1].row);
    const dc = Math.abs(tiles[i].col - tiles[i - 1].col);
    if (dr <= 1 && dc <= 1 && (dr + dc) > 0) {
      count++;
    }
  }
  return count;
}

/** Minimum word length to qualify for adjacency bonus. */
export const ADJ_MIN_LENGTH = 3;

/**
 * Adjacency bonus multiplier.
 *
 * By default (partial = false): all-or-nothing — every consecutive pair
 * in the selection must be adjacent, AND the word must be at least
 * ADJ_MIN_LENGTH tiles long. If any pair breaks the chain, no bonus.
 *
 * With partial = true: each adjacent pair contributes independently
 * (for future perks/effects that reward partial chains).
 *
 * bonusPerPair increases the per-pair rate (e.g. Chain Link perk adds +0.1).
 */
export function adjacencyMultiplier(
  tiles: { row: number; col: number }[],
  bonusPerPair: number = 0,
  partial: boolean = false,
): number {
  if (tiles.length < ADJ_MIN_LENGTH) return 1;
  const pairs = countAdjacentPairs(tiles);
  if (pairs === 0) return 1;

  if (!partial) {
    // All-or-nothing: every pair must be adjacent
    if (pairs < tiles.length - 1) return 1;
  }

  return 1 + pairs * (0.1 + bonusPerPair);
}
